import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { webSocketService } from "../services/websocketService";
import {
    Filter,
    Search,
    Clock,
    CheckCircle,
    XCircle,
    MessageCircle,
    Calendar,
    ChevronDown,
    FileText,
    AlertCircle,
    X,
    Send,
    Flag,
    Paperclip,
    Plus,
    RefreshCcw,
    MoreVertical,
    ArrowUpRight,
    MessageSquare,
    Home,
    User,
    Settings,
    Shield,
    ChartAreaIcon,
    Menu,
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import {
    createChat,
    createConversation,
    createUserTickets,
    EmptyConversations,
    getUserChats,
    getUserConversations,
    getUserTickets,
    UpdateUserChats,
    useAppDispatch,
} from "../redux";
import { useSelector } from "react-redux";
import { CategoryEnum, ContextEnum, PriorityEnum, RoleEnum, TicketStatusesEnum } from "../modules/shared/model";
import NewTicketModal from "../client/Modals/NewTicketModal";
import Spinner from "../modules/shared/components/Spinner";
import { hideLoader, selectLoaderValue, showLoader } from "../redux/Slices/loadingSlice";
import TicketDetailsModal from "../modules/user/modals/TicketDetailModal";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import { useNavigate } from "react-router-dom";

const SupportTickets = () => {
    // State management
    const { t } = useTranslation();
    const isLoading = useSelector(selectLoaderValue);
    const [activeTab, setActiveTab] = useState("open");
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [dateRange, setDateRange] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [newMessage, setNewMessage] = useState("");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showNewTicketModal, setShowNewTicketModal] = useState(false);
    const [attachments, setAttachments] = useState<any>([]);
    const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
    const [newTicket, setNewTicket] = useState({
        subject: "",
        description: "",
        category: "",
        priority: "",
    });

    const { user, userTickets, chats, conversations, token } = useSelector((state: any) => state.auth);
    const dispatch = useAppDispatch();

    useEffect(() => {
        getAllCurrentUserTickets();
    }, []);

    // Real-time WebSocket listener for support ticket messages
    useEffect(() => {
        if (!user?.id) return;

        let subscriptionId: string | null = null;

        const handleWebSocketMessage = (message: any) => {
            console.log("📨 [Professional Support] WebSocket message received:", message);
            
            // Handle new support messages
            if (message.type === "new_support_message" || message.type === "support_ticket_update") {
                const { ticketId, chatId, senderId, message: messageText } = message.detail || {};
                
                // Check if the message is from admin/support (not from the current user)
                const isFromAdmin = senderId && senderId !== user.id;
                
                // If we have a selected ticket and it matches the incoming message
                if (selectedTicket && selectedTicket.id === ticketId) {
                    console.log("🔄 [Professional Support] Refreshing conversations for active ticket");
                    // Refresh conversations for the current ticket immediately
                    if (chatId || chats?.id) {
                        dispatch(getUserConversations({ id: chatId || chats?.id })).then(() => {
                            console.log("✅ [Professional Support] Conversations refreshed successfully");
                        }).catch((err) => {
                            console.error("❌ [Professional Support] Failed to refresh conversations:", err);
                        });
                    }
                    
                    console.log("ℹ️ [Professional Support] Real-time message received - notification handled by backend");
                } else {
                    console.log("ℹ️ [Professional Support] Message ignored (own message or no ticket match)");
                }
                
                // Refresh the tickets list to update any status/priority changes
                getAllCurrentUserTickets();
            }
        };

        // Subscribe to WebSocket messages by connecting with message callback
        webSocketService.connect({
            userId: user.id,
            token: token || "",
            onMessage: handleWebSocketMessage,
        }).then((subId) => {
            subscriptionId = subId;
            console.log("✅ [Professional Support] WebSocket subscription registered:", subId);
        }).catch((error) => {
            console.warn("⚠️ [Professional Support] Failed to register WebSocket subscription:", error);
        });

        return () => {
            if (subscriptionId) {
                webSocketService.removeCallbacksById(subscriptionId);
                console.log("🗑️ [Professional Support] WebSocket subscription removed");
            }
        };
    }, [user?.id, selectedTicket, chats, dispatch, token]);

    const getAllCurrentUserTickets = async () => {
        await dispatch(getUserTickets({ id: user?.id }));
    };

    // Check if user needs to complete profile
    const checkProfileCompletion = () => {
        if (user && !user.phone) {
            setShowProfileCompletionDialog(true);
            return false; // Profile incomplete
        }
        return true; // Profile complete
    };

    // Helper function to navigate to profile based on user role
    const navigateToProfile = () => {
        if (!user?.role?.id) return;
        
        const roleId = Number(user.role.id);
        if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
            navigate("/myprofile");
        } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
            navigate("/client/profile");
        } else if (roleId === RoleEnum.admin) {
            navigate("/admin/dashboard");
        }
        setShowProfileCompletionDialog(false);
    };
    const mapApiTicketsToLocal = (apiTickets: any[], user: any) => {
        return apiTickets.map(ticket => ({
            id: ticket.id,
            subject: ticket.subject,
            description: ticket.description,
            status: ticket.status,
            priority: ticket.priority,
            category: ticket.category,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt,
            userId: ticket.userId, // Preserve the user info from API
            messages: [
                // {
                //     id: 1,
                //     sender: `${user?.firstName} ${user?.lastName}`,
                //     senderType: "user", // Add this field
                //     role: "client",
                //     message: ticket.description,
                //     timestamp: ticket.createdAt,
                //     attachments: [],
                // },
                // {
                //     id: 2,
                //     sender: "Support Team",
                //     role: "support",
                //     senderType: "support", // Add this field
                //     message: "Thank you for reporting this issue. Could you please provide more details?",
                //     timestamp: new Date(new Date(ticket.createdAt).getTime() + 3600000).toISOString(), // 1 hour after creation
                //     attachments: [],
                // },
            ],
            conversations: conversations,
            assignedTo: "Sarah Wilson",
            tags: ["payment", "urgent"],
            assignee: "Support Team",
            department: getCategoryName(ticket.category.id),
        }));
    };
    const [tickets, setTickets] = useState<any[]>([]);

    useEffect(() => {
        if (userTickets && userTickets.length > 0) {
            setTickets(mapApiTicketsToLocal(userTickets, user));
        } else {
            setTickets([]);
        }
    }, [userTickets, user]);
    const handleCreateTicket = async () => {
        // Check if user needs to complete profile
        if (!checkProfileCompletion()) {
            return;
        }

        try {
            const payload = {
                category: {
                    id: newTicket.category,
                },
                userId: {
                    id: user.id,
                },
                priority: {
                    id: newTicket.priority,
                },
                status: {
                    id: TicketStatusesEnum.Open, // Default to Open status
                },
                description: newTicket.description,
                subject: newTicket.subject,
            };

            await dispatch(createUserTickets({ data: payload }));

            setShowNewTicketModal(false);
            setNewTicket({
                subject: "",
                description: "",
                category: "",
                priority: "",
            });

            // Refresh tickets list
            await getAllCurrentUserTickets();
        } catch (error) {
            throw error;
        }
    };

    // Helper functions to get names and colors
    const getCategoryName = (categoryId: number) => {
        switch (Number(categoryId)) {
            case CategoryEnum.Technical:
                return "Technical Support";
            case CategoryEnum.Billing:
                return "Billing Support";
            case CategoryEnum.General:
                return "General Support";
            case CategoryEnum.Payment:
                return "Payment Support";
            default:
                return "General Support";
        }
    };

    const getStatusName = (statusId: number) => {
        return TicketStatusesEnum[statusId] || "Open";
    };

    const getPriorityName = (priorityId: number) => {
        return PriorityEnum[priorityId] || "Medium";
    };

    const getStatusColor = (statusId: number) => {
        switch (statusId) {
            case TicketStatusesEnum.Open:
                return "bg-green-100 text-green-800";
            case TicketStatusesEnum.In_Progress:
                return "bg-blue-100 text-blue-800";
            case TicketStatusesEnum.Resolved:
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getPriorityColor = (priorityId: number) => {
        switch (priorityId) {
            case PriorityEnum.High:
                return "bg-red-100 text-red-800";
            case PriorityEnum.Medium:
                return "bg-yellow-100 text-yellow-800";
            case PriorityEnum.Low:
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };
    const getRoleName = (roleId: number) => {
        return RoleEnum[roleId] || "Support_Ticket";
    };
    // Status badge component
    const StatusBadge = ({ statusId }) => {
        const statusName = getStatusName(Number(statusId));
        const statusColor = getStatusColor(Number(statusId));

        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                {statusName.replace("_", " ")}
            </span>
        );
    };

    // Priority badge component
    const PriorityBadge = ({ priorityId }) => {
        const priorityName = getPriorityName(Number(priorityId));
        const priorityColor = getPriorityColor(Number(priorityId));

        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColor}`}>{priorityName}</span>;
    };

    // Filter tickets based on active tab and search query
    const filteredTickets = tickets?.filter(ticket => {
        // Convert string IDs to numbers for comparison
        const statusId = Number(ticket.status.id);
        const priorityId = Number(ticket.priority.id);
        const categoryId = Number(ticket.category.id);

        // Filter by active tab
        if (activeTab === "open" && statusId !== TicketStatusesEnum.Open) return false;
        if (activeTab === "in_progress" && statusId !== TicketStatusesEnum.In_Progress) return false;
        if (activeTab === "resolved" && statusId !== TicketStatusesEnum.Resolved) return false;

        // Filter by search query
        if (searchQuery && !ticket.subject.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
        }

        // Filter by status
        if (statusFilter !== "all" && statusId !== Number(statusFilter)) {
            return false;
        }

        // Filter by priority
        if (priorityFilter !== "all" && priorityId !== Number(priorityFilter)) {
            return false;
        }

        // Filter by category
        if (categoryFilter !== "all" && categoryId !== Number(categoryFilter)) {
            return false;
        }

        return true;
    });
    // Sort tickets
    const sortedTickets = filteredTickets?.sort((a, b) => {
        if (sortBy === "newest") {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        } else if (sortBy === "oldest") {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        } else if (sortBy === "priority") {
            return b.priority.id - a.priority.id;
        } else {
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        }
    });
    const onSendMessage = async () => {
        if (newMessage.trim() || attachments.length > 0) {
            try {
                dispatch(showLoader());
                let chatResponse;
                // First create the chat
                const chatPayload = {
                    unreadCount: {},
                    participants: [{ id: user?.id }],
                    contextId: selectedTicket.id,
                    contextType: { id: ContextEnum.Support_Ticket },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: newMessage || `Sent ${attachments.length} attachment(s)`,
                };
                if (chats != null) {
                    chatResponse = await dispatch(UpdateUserChats({ id: chats?.id, data: chatPayload })).unwrap();
                } else {
                    chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
                }

                // Then create the conversation with attachments
                const conversationPayload = {
                    isRead: false,
                    message: newMessage,
                    sender: { id: user?.id },
                    chatId: { id: chatResponse.id },
                    attachments: attachments.map(att => ({ id: att.id })),
                };

                await dispatch(createConversation({ data: conversationPayload }));
                
                // Immediately refresh conversations to show the new message
                await dispatch(getUserConversations({ id: chatResponse.id }));
                
                // Send WebSocket message to notify admin/support
                try {
                    const receivers = ["admin"]; // Notify admin users
                    if (webSocketService.getConnectionStatus()) {
                        await webSocketService.sendMessage({
                            receivers,
                            sender: user?.id,
                            type: "new_support_message",
                            detail: {
                                conversationId: conversationPayload.chatId.id,
                                chatId: conversationPayload.chatId.id,
                                message: newMessage,
                                senderId: user?.id,
                                ticketId: selectedTicket.id,
                                timestamp: new Date().toISOString()
                            }
                        });
                        console.log("📤 WebSocket message sent for support ticket");
                    }
                } catch (wsError) {
                    console.warn("⚠️ Failed to send WebSocket message for support ticket:", wsError);
                }

                // Clear input fields
                setNewMessage("");
                setAttachments([]);
            } catch (error) {
                throw error;
            } finally {
                dispatch(hideLoader());
            }
        }
    };
    const getCurrentChatData = async (ticket: any) => {
        const { payload } = await dispatch(getUserChats({ id: ticket?.id }));
        if (payload?.id) {
            await dispatch(getUserConversations({ id: payload?.id }));
        }
        setSelectedTicket(ticket);
    };
    const formatDate = dateString => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };
    return (
        <>
            {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )}
            <div className={`min-h-screen bg-gray-50 ${isLoading ? "opacity-25" : "opacity-100"}`}>
                {/* Fixed Header */}
                <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16">
                    <SharedHeader4 />
                </div>

                <div className="flex min-h-[calc(100vh-64px)] pt-16">
                    {/* Main Content */}
                    <main className="flex-1 px-6 py-8 max-w-6xl mx-auto">
                        <div className="mb-8">
                            <h1 className="text-2xl font-bold text-gray-900">{t("supportTickets.title")}</h1>
                            <p className="text-gray-600 mt-1">{t("supportTickets.manageAndRespond")}</p>
                        </div>

                        {/* Ticket Stats */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            <Card className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <MessageSquare className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <span className="text-sm text-gray-500">{t("supportTickets.totalTickets")}</span>
                                </div>
                                <div className="mt-4">
                                    <h3 className="text-2xl font-bold text-gray-900">{tickets?.length || 0}</h3>
                                    <p className="text-sm text-gray-600">{t("supportTickets.allTickets")}</p>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <Clock className="h-5 w-5 text-green-600" />
                                    </div>
                                    <span className="text-sm text-gray-500">{t("supportTickets.open")}</span>
                                </div>
                                <div className="mt-4">
                                    <h3 className="text-2xl font-bold text-gray-900">
                                        {userTickets?.filter(t => t.status.id == TicketStatusesEnum.Open).length || 0}
                                    </h3>
                                    <p className="text-sm text-gray-600">{t("supportTickets.activeTickets")}</p>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="p-2 bg-yellow-100 rounded-lg">
                                        <Flag className="h-5 w-5 text-yellow-600" />
                                    </div>
                                    <span className="text-sm text-gray-500">{t("supportTickets.highPriority")}</span>
                                </div>
                                <div className="mt-4">
                                    <h3 className="text-2xl font-bold text-gray-900">
                                        {userTickets?.filter(t => t.priority.id == PriorityEnum.High).length || 0}
                                    </h3>
                                    <p className="text-sm text-gray-600">{t("supportTickets.needAttention")}</p>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <CheckCircle className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <span className="text-sm text-gray-500">{t("supportTickets.resolved")}</span>
                                </div>
                                <div className="mt-4">
                                    <h3 className="text-2xl font-bold text-gray-900">
                                        {userTickets?.filter(t => t.status.id == TicketStatusesEnum.Resolved).length ||
                                            0}
                                    </h3>
                                    <p className="text-sm text-gray-600">{t("supportTickets.completedTickets")}</p>
                                </div>
                            </Card>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex flex-wrap gap-4 mb-8">
                            <button
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                onClick={() => setShowNewTicketModal(true)}>
                                <Plus className="h-5 w-5" />
                                {t("supportTickets.createTicket")}
                            </button>
                            <button
                                className="flex items-center gap-2 px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50"
                                onClick={getAllCurrentUserTickets}>
                                <RefreshCcw className="h-5 w-5" />
                                {t("supportTickets.refreshList")}
                            </button>
                        </div>

                        {/* Filters and Search */}
                        <Card className="mb-8">
                            <div className="p-4 border-b">
                                <div className="flex flex-col sm:flex-row justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="relative flex-1 sm:flex-none sm:min-w-[300px]">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder={t("supportTickets.searchTickets")}
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                            />
                                        </div>
                                        <button
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
                                            <Filter className="h-5 w-5" />
                                            <span>{t("supportTickets.filters")}</span>
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <select
                                            value={sortBy}
                                            onChange={e => setSortBy(e.target.value)}
                                            className="px-4 py-2 border rounded-lg">
                                            <option value="newest">{t("supportTickets.newestFirst")}</option>
                                            <option value="oldest">{t("supportTickets.oldestFirst")}</option>
                                            <option value="priority">{t("supportTickets.priority")}</option>
                                            <option value="updated">{t("supportTickets.lastUpdated")}</option>
                                        </select>
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {showFilters && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="mt-4 pt-4 border-t">
                                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                                                {/* Status Filter */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {t("supportTickets.status")}
                                                    </label>
                                                    <select
                                                        value={statusFilter}
                                                        onChange={e => setStatusFilter(e.target.value)}
                                                        className="px-3 py-2 border rounded-lg">
                                                        <option value="all">{t("supportTickets.allStatus")}</option>
                                                        {Object.keys(TicketStatusesEnum)
                                                            .filter(key => isNaN(Number(key)))
                                                            .map(key => (
                                                                <option key={key} value={TicketStatusesEnum[key]}>
                                                                    {key.replace("_", " ")}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>

                                                {/* Priority Filter */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {t("supportTickets.priority")}
                                                    </label>
                                                    <select
                                                        value={priorityFilter}
                                                        onChange={e => setPriorityFilter(e.target.value)}
                                                        className="w-full px-4 py-2 border rounded-lg">
                                                        <option value="all">{t("supportTickets.allPriority")}</option>
                                                        {Object.keys(PriorityEnum)
                                                            .filter(key => isNaN(Number(key)))
                                                            .map(key => (
                                                                <option key={key} value={PriorityEnum[key]}>
                                                                    {key}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>

                                                {/* Category Filter */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {t("supportTickets.category")}
                                                    </label>
                                                    <select
                                                        value={categoryFilter}
                                                        onChange={e => setCategoryFilter(e.target.value)}
                                                        className="w-full px-4 py-2 border rounded-lg">
                                                        <option value="all">{t("supportTickets.allCategories")}</option>
                                                        {Object.keys(CategoryEnum)
                                                            .filter(key => isNaN(Number(key)))
                                                            .map(key => (
                                                                <option key={key} value={CategoryEnum[key]}>
                                                                    {key.replace("Support", "")}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>

                                                {/* Date Range Filter */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {t("supportTickets.dateRange")}
                                                    </label>
                                                    <select
                                                        value={dateRange}
                                                        onChange={e => setDateRange(e.target.value)}
                                                        className="w-full px-4 py-2 border rounded-lg">
                                                        <option value="all">{t("supportTickets.allTime")}</option>
                                                        <option value="today">{t("supportTickets.today")}</option>
                                                        <option value="week">{t("supportTickets.thisWeek")}</option>
                                                        <option value="month">{t("supportTickets.thisMonth")}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="flex justify-end mt-4">
                                                <button
                                                    onClick={() => {
                                                        setDateRange("all");
                                                        setStatusFilter("all");
                                                        setPriorityFilter("all");
                                                        setCategoryFilter("all");
                                                        setShowFilters(false);
                                                    }}
                                                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                                    {t("supportTickets.resetFilters")}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Tickets List */}
                            <div className="divide-y">
                                {sortedTickets?.length > 0 ? (
                                    sortedTickets.map(ticket => (
                                        <div
                                            key={ticket.id}
                                            className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => {
                                                getCurrentChatData(ticket);
                                            }}>
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                                        {ticket.userId.firstName?.[0]}
                                                        {ticket.userId.lastName?.[0]}
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-medium text-gray-900">
                                                            {ticket.subject}
                                                        </h3>
                                                        <p className="text-sm text-gray-600">
                                                            {ticket.userId.firstName} {ticket.userId.lastName} • #
                                                            {ticket.id}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <StatusBadge statusId={ticket.status.id} />
                                                    <PriorityBadge priorityId={ticket.priority.id} />
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                                                {/* <div className="flex items-center gap-2">
                                                    <MessageCircle className="h-4 w-4" />
                                                    {ticket?.conversations?.length} messages
                                                </div> */}
                                                <div>
                                                    <span className="font-medium">Category:</span>{" "}
                                                    {getCategoryName(ticket.category.id)}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Created:</span>{" "}
                                                    {new Date(ticket.createdAt).toLocaleDateString()}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Last updated:</span>{" "}
                                                    {new Date(ticket.updatedAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-6 text-center text-gray-500">{t("supportTickets.noTicketsFound")}</div>
                                )}
                            </div>
                        </Card>

                        {/* Ticket Details Modal */}
                        <AnimatePresence>
                            {selectedTicket && (
                                <TicketDetailsModal
                                    ticket={selectedTicket}
                                    onClose={() => {
                                        setSelectedTicket(null);
                                        dispatch(EmptyConversations());
                                    }}
                                    user={user}
                                    getRoleName={getRoleName}
                                    formatDate={formatDate}
                                    newMessage={newMessage}
                                    setNewMessage={setNewMessage}
                                    onSendMessage={onSendMessage}
                                    attachments={attachments}
                                    setAttachments={setAttachments}
                                    StatusBadge={StatusBadge}
                                    PriorityBadge={PriorityBadge}
                                    getCategoryName={getCategoryName}
                                    conversations={conversations}
                                    getPriorityName={getPriorityName}
                                />
                            )}
                        </AnimatePresence>
                    </main>
                </div>

                {/* New Ticket Modal */}
                <NewTicketModal
                    show={showNewTicketModal}
                    onClose={() => setShowNewTicketModal(false)}
                    onSubmit={handleCreateTicket}
                    newTicket={newTicket}
                    setNewTicket={setNewTicket}
                />

                {/* Profile Completion Dialog */}
                <ProfileCompletionDialog
                    isOpen={showProfileCompletionDialog}
                    onClose={() => setShowProfileCompletionDialog(false)}
                    onGoToProfile={navigateToProfile}
                    userRole={user?.role?.id}
                />

                <br />
                <br />
                <br />
                <br />

                <SharedFooter3 />
            </div>
        </>
    );
};

export default SupportTickets;